import React, { useCallback, useContext, useState } from 'react';

import {
  Modal,
  Button,
  Typography,
  Divider,
  message,
  InputNumber,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import { formatCurrency, toBytes } from '@utils/formatters';
import { DollarOutlined } from '@ant-design/icons';
import SessionContext from '@providers/Session/SessionContext';
import './styles.less';

const { Paragraph } = Typography;

interface IAddCredit {
  creditType: 'sim' | 'contract';
  idToAdd: number;
  visible: boolean;
  onClose: Function;
  isPurchase?: boolean;
}

const AddCredit: React.FC<IAddCredit> = ({
  creditType,
  idToAdd,
  visible,
  onClose,
  isPurchase,
}) => {
  const { t } = useTranslation('credits');
  const { theme } = useContext(SessionContext);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [franchise, setFranchise] = useState(0);
  const [franchiseUnit, setFranchiseUnit] = useState<'KB' | 'MB' | 'GB'>('MB');

  const [getCreditCostLoading, setGetCreditCostLoading] = useState(false);
  const [creditPurchaseCost, setCreditPurchaseCost] = useState();

  const postCreditSim = useCallback(
    (id: number, payload: any) =>
      api.post(`/service-proxy/sims/${id}/credit`, payload),
    [],
  );

  const postCreditContract = useCallback(
    (id: number, creditBytes: number) =>
      api.post(
        `/service-proxy/${
          isPurchase ? '' : 'broker/'
        }virtual-plan/${id}/credit?creditBytes=${creditBytes}`,
      ),
    [isPurchase],
  );

  const calculateCreditCost = useCallback(async () => {
    setGetCreditCostLoading(true);
    try {
      const unit = franchiseUnit;
      const creditBytes = toBytes(franchise, unit);

      const { data } = await api.get(
        `/service-proxy/virtual-plan/${idToAdd}/credit-cost?creditBytes=${creditBytes}`,
      );

      setCreditPurchaseCost(data?.value);
    } catch (err) {
      message.error(t('msgs.error-when-calculating'));
    }
    setGetCreditCostLoading(false);
  }, [franchise, franchiseUnit, idToAdd, t]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleFinish = useCallback(async () => {
    setConfirmLoading(true);
    try {
      const data = {
        franchiseBytes: toBytes(franchise, franchiseUnit),
      };
      if (creditType === 'sim') {
        await postCreditSim(idToAdd, data);
      } else {
        await postCreditContract(idToAdd, data?.franchiseBytes);
      }
      message.success(t('msgs.success-to-add'));
      handleClose();
    } catch (error) {
      // @ts-ignore
      const status = error?.response?.status;
      if (status === 404) {
        if (creditType === 'sim') {
          message.error(t('msgs.request-not-found-sim'));
        } else {
          message.error(t('msgs.request-not-found'));
        }
      } else {
        message.error(t('msgs.error-to-add'));
      }
    }
    setConfirmLoading(false);
  }, [
    franchise,
    franchiseUnit,
    creditType,
    t,
    handleClose,
    postCreditSim,
    idToAdd,
    postCreditContract,
  ]);

  return (
    <Modal
      bodyStyle={{ minHeight: 180, padding: 24 }}
      footer={
        <Button
          loading={confirmLoading}
          type="primary"
          onClick={handleFinish}
          disabled={franchise === 0}
        >
          {t('confirm')}
        </Button>
      }
      maskClosable={false}
      okButtonProps={{ form: 'customerForm', htmlType: 'submit' }}
      style={{ maxWidth: 480 }}
      title={t(isPurchase ? 'credit-purchase' : 'add-credit')}
      open={visible}
      width="100%"
      centered
      onCancel={handleClose}
      afterClose={() => document.body.style.removeProperty('overflow')}
    >
      <Paragraph>{t('description')}</Paragraph>
      <Divider />
      <div style={{ textAlign: 'center' }}>
        <InputNumber
          defaultValue={franchise}
          min={0}
          style={{ width: '100%', maxWidth: 140, margin: '0 auto' }}
          // @ts-ignore
          onChange={setFranchise}
          addonAfter={
            <Select
              value={franchiseUnit}
              onChange={setFranchiseUnit}
              options={['KB', 'MB', 'GB'].map(item => ({
                label: item,
                value: item,
              }))}
              style={{ width: 64 }}
            />
          }
        />
      </div>

      {isPurchase && (
        <>
          <Divider />
          <div style={{ padding: 16, textAlign: 'center' }}>
            <Button
              type={creditPurchaseCost ? 'default' : 'primary'}
              icon={<DollarOutlined />}
              onClick={calculateCreditCost}
              loading={getCreditCostLoading}
              disabled={!franchise}
            >
              {t('calculate-cost')}
            </Button>
            {(!!creditPurchaseCost || creditPurchaseCost === 0) && (
              <div
                style={{
                  display: 'inline-flex',
                  marginLeft: 8,
                  padding: '4px 16px',
                  borderRadius: 8,
                  fontSize: '1rem',
                  border: `1px dashed ${theme?.colors?.border}`,
                  background: theme?.colors?.background1,
                  color: 'var(--primary-color)',
                }}
              >
                <span>{formatCurrency(creditPurchaseCost, 2)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddCredit;
